/* eslint-disable max-lines */
import { useSetAtom } from "jotai";
import { useState } from "react";
import { useWatch } from "react-hook-form";

import { useArtworkForm, useClearManualEdits } from "@design-lab/entities/artwork";
import { UnionIcon } from "@design-lab/entities/quality-tabs";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import { trackRemoveBackgroundClicked } from "@design-lab/shared/lib/analytics";
import { Button, Label } from "@design-lab/shared/ui";
import { SwitchInput } from "@design-lab/shared/ui/SwitchInput";
import { refineArtworkOpenAtom } from "@design-lab/widgets/artboard/model/state";

export function RemoveBackground() {
  const { form, lens } = useArtworkForm();
  const clearManualEdits = useClearManualEdits();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const currentPlacement = useWatch({
    control: form.control,
    name: "currentPlacement"
  });
  const side = currentPlacement === "back" ? "back" : "front";
  const artworkId = useWatch({
    control: form.control,
    name: `${side}.artworkId`
  });
  const editImageManual = useWatch({
    control: form.control,
    name: `info.${side}.editImageManual`
  });
  const removeBackground = useWatch({
    control: form.control,
    name: `info.${side}.removeBackground`
  });
  const openRefine = useSetAtom(refineArtworkOpenAtom);

  return (
    <div className="flex h-10 w-full items-center gap-1 rounded-md bg-white p-1 shadow-sm">
      <Button
        data-testid="remove-background-button"
        variant="outline"
        size="lg"
        className="relative border-none bg-transparent shadow-none"
      >
        <Label
          data-testid="remove-background-label"
          htmlFor="remove-background"
          className="absolute left-0 top-0 h-full w-full cursor-pointer"
        />

        <div className="mr-2 flex items-center gap-0.5">Background</div>

        <SwitchInput
          data-testid="remove-background-switch"
          id="remove-background"
          lens={lens.focus(`info.${side}.removeBackground`)}
          onCheckedChange={checked => {
            if (!artworkId) return;

            // Show confirmation if there are manual edits that will be cleared
            if (editImageManual) {
              form.setValue(`info.${side}.removeBackground`, !checked);
              setIsConfirmModalOpen(true);
              return;
            }

            clearManualEdits(side);

            trackRemoveBackgroundClicked({
              source: "canvas",
              printQuality: "PrintPerfect",
              side,
              artworkId
            });
          }}
        />
      </Button>
      <Button
        type="button"
        data-testid="refine-artwork-button"
        className="bg-cta-gradient hover:bg-cta-gradient-hover flex h-8 cursor-pointer items-center justify-center gap-2 rounded-md border-0 p-1.5 px-3 text-sm text-white"
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          openRefine(true);
        }}
      >
        <UnionIcon className="h-4 w-4 text-white" />
        <span>Edit Design</span>
      </Button>
      <ConfirmActionModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title={`Turn ${removeBackground ? "Off" : "On"} Background Removal?`}
        message={`${removeBackground ? "Disabling" : "Enabling"} this will undo all background edits you've made. Do you want to continue?`}
        confirmText={`Turn ${removeBackground ? "Off" : "On"}`}
        cancelText="Keep Edits"
        onConfirm={() => {
          if (!artworkId) return;

          // Toggle to the opposite of current state
          const currentValue = form.getValues(`info.${side}.removeBackground`);
          form.setValue(`info.${side}.removeBackground`, !currentValue);
          clearManualEdits(side);
          trackRemoveBackgroundClicked({
            source: "canvas",
            printQuality: "PrintPerfect",
            side,
            artworkId
          });
          setIsConfirmModalOpen(false);
        }}
      />
    </div>
  );
}
